import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import {
    getSafetyProjectDetail,
    getAssessProcesses,
    getAssessEvaluations,
    getSafetyResultSummary,
    getSafetyMeetings,
    getSafetyTrainings,
    getSafetyPreparation,
    getSafetyBasicInfo,
    getSafetyProjectYears,
    getSafetyProjectsOptimized
} from '../../../api/safetyAssessApi';
import SafetyProjectModal from './steps/SafetyProjectModal';
import SafetyReportViewerModal from './steps/report/SafetyReportViewerModal';
import SafetyReportSectionSelectModal from './steps/report/SafetyReportSectionSelectModal';
import SafetyReportMeetingModal from './steps/report/SafetyReportMeetingModal';
import SafetyReportTrainingModal from './steps/report/SafetyReportTrainingModal';
import SafetyReportMeetingListModal from './steps/report/SafetyReportMeetingListModal';
import SafetyReportTrainingListModal from './steps/report/SafetyReportTrainingListModal';
import SafetyReportApprovalModal from './steps/report/SafetyReportApprovalModal';
import SafetyReportApprovalListModal from './steps/report/SafetyReportApprovalListModal';
import SafetyReportConclusionModal from './steps/report/SafetyReportConclusionModal';
import '../Safety.css';

const renderNamePositionBadge = (fullName, isManager = false) => {
    if (!fullName || fullName === '-') return '-';
    const cleanName = fullName.replace(/\s*\/?\(?관리감독자\)?/g, '').replace(/\(.*?\)/g, '').trim();
    const parts = cleanName.split(/\s+/);
    const name = parts[0] || '';
    const pos = parts.slice(1).join(' ') || '';
    const displayText = pos ? `${name} / ${pos}` : name;
    const iconClass = isManager ? 'fas fa-user-check' : 'fas fa-user';
    return (
        <span className="badge bg-light text-dark border px-2 py-1.5 fw-semibold" style={{ fontSize: '0.8rem', borderColor: '#cbd5e1', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
            <i className={`${iconClass} me-1 text-secondary`} style={{ fontSize: '0.85rem' }}></i>
            {displayText}
        </span>
    );
};

function SafetyResultList() {
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);
    const [projects, setProjects] = useState([]);
    const [filterYear, setFilterYear] = useState(() => new Date().getFullYear().toString());
    const [availableYears, setAvailableYears] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [allCompleted, setAllCompleted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeModal, setActiveModal] = useState(null); // 'TOTAL_PDF', 'MINUTES', 'TRAINING'
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewModalData, setViewModalData] = useState(null);

    // 종합보고서 뷰어 모달 상태
    const [reportViewerShow, setReportViewerShow] = useState(false);
    const [reportViewerData, setReportViewerData] = useState(null);
    const [printSections, setPrintSections] = useState({
        cover: true,
        prep: true,
        evals: true,
        photos: true,
        conclusion: true,
        meetings: true,
        trainings: true
    });

    // 필터, 검색, 페이징 상태
    const [filterType, setFilterType] = useState('ALL');
    const [searchType, setSearchType] = useState('ALL');
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('completed');
    const itemsPerPage = 10;

    // 현재 모달에 바인딩할 상세 데이터 상태들
    const [selectedProj, setSelectedProj] = useState(null);
    const [modalMeetings, setModalMeetings] = useState([]);
    const [modalTrainings, setModalTrainings] = useState([]);

    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [selectedTraining, setSelectedTraining] = useState(null);
    const [selectedSummary, setSelectedSummary] = useState(null);

    // 전자결재 전용 인쇄 관련
    const [modalApprovals, setModalApprovals] = useState([]);
    const [selectedApprovalDoc, setSelectedApprovalDoc] = useState(null);

    const [modalLoading, setModalLoading] = useState(false);

    const displayMeetingsForModal = Array.isArray(modalMeetings) ? modalMeetings : (modalMeetings?.items || []);
    const displayTrainingsForModal = Array.isArray(modalTrainings) ? modalTrainings : (modalTrainings?.items || []);

    // DB에 존재하는 프로젝트 연도 목록 조회
    useEffect(() => {
        const fetchYears = async () => {
            if (!token) return;
            try {
                const years = await getSafetyProjectYears(token);
                if (years && years.length > 0) {
                    setAvailableYears(years);
                    const currentYear = new Date().getFullYear().toString();
                    if (!years.includes(parseInt(currentYear, 10)) && !years.includes(currentYear)) {
                        setFilterYear(years[0].toString());
                    }
                }
            } catch (err) {
                console.error('fetchYears error:', err);
            }
        };
        fetchYears();
    }, [token]);

    // 선택 상태 프로젝트(탭 카운트용)
    const fetchAllCompleted = useCallback(async () => {
        if (!token) return;
        try {
            const params = { status: statusFilter };
            if (filterYear && filterYear !== 'ALL') {
                params.year = filterYear;
            }
            const data = await getSafetyProjectsOptimized(token, params);
            if (data && data.data) {
                setAllCompleted(data.data);
            } else {
                const arr = Array.isArray(data) ? data : [];
                setAllCompleted(arr.filter(p => p.status === statusFilter));
            }
        } catch (err) {
            console.error(err);
        }
    }, [token, filterYear, statusFilter]);

    // 서버사이드 페이징/검색 조회 (상태 필터 반영)
    const fetchArchive = useCallback(async () => {
        if (!token) return;
        try {
            const params = { page: currentPage, limit: itemsPerPage, status: statusFilter };
            if (filterType !== 'ALL') params.filterType = filterType;
            if (searchTerm) {
                params.searchType = searchType;
                params.searchTerm = searchTerm;
            }
            if (filterYear && filterYear !== 'ALL') {
                params.year = filterYear;
            }
            const result = await getSafetyProjectsOptimized(token, params);
            if (result && result.data) {
                setProjects(result.data);
                setTotalCount(result.total);
            } else {
                const arr = Array.isArray(result) ? result : [];
                const completed = arr.filter(p => p.status === statusFilter);
                setProjects(completed);
                setTotalCount(completed.length);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [token, currentPage, filterType, searchType, searchTerm, filterYear, statusFilter]);

    useEffect(() => { fetchAllCompleted(); }, [fetchAllCompleted]);
    useEffect(() => { fetchArchive(); }, [fetchArchive]);

    const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

    // 검색어 필터링을 반영한 탭 카운팅용 데이터셋 생성
    const filteredForCount = allCompleted.filter(proj => {
        if (!searchTerm) return true;
        const val = searchTerm.toLowerCase();
        const nameMatch = proj.name?.toLowerCase().includes(val);
        const siteMatch = proj.site_name?.toLowerCase().includes(val);
        const managerMatch = proj.manager_name?.toLowerCase().includes(val);
        const writerMatch = proj.write_name?.toLowerCase().includes(val);

        if (searchType === 'NAME') {
            return nameMatch;
        } else if (searchType === 'SITE') {
            return siteMatch;
        } else if (searchType === 'MANAGER') {
            return managerMatch || writerMatch;
        } else {
            // ALL
            return nameMatch || siteMatch || managerMatch || writerMatch;
        }
    });

    // 특정 보고서 유형 인쇄 모달 열기 (관련 데이터 유형별 선택적 수집)
    const handleOpenPrintModal = async (project, modalType) => {
        setSelectedProj(project);
        setModalMeetings([]);
        setModalTrainings([]);
        setSelectedMeeting(null);
        setSelectedTraining(null);
        setModalLoading(true);

        if (modalType === 'MINUTES' || modalType === 'TRAINING') {
            setActiveModal(modalType);
        }

        try {
            if (modalType === 'MINUTES') {
                const meetings = await getSafetyMeetings(project.id, token);
                const finalMeetings = Array.isArray(meetings) ? meetings : (meetings?.items || []);
                setModalMeetings(finalMeetings);

                if (finalMeetings.length === 0) {
                    alert('등록된 회의록이 없습니다.');
                    setActiveModal(null);
                } else if (finalMeetings.length === 1) {
                    setSelectedMeeting(finalMeetings[0]);
                    setActiveModal('SINGLE_MINUTE');
                } else {
                    setActiveModal('MINUTES');
                }
            } else if (modalType === 'TRAINING') {
                const trainings = await getSafetyTrainings(project.id, token);
                const finalTrainings = Array.isArray(trainings) ? trainings : (trainings?.items || []);
                setModalTrainings(finalTrainings);

                if (finalTrainings.length === 0) {
                    alert('등록된 교육일지가 없습니다.');
                    setActiveModal(null);
                } else if (finalTrainings.length === 1) {
                    setSelectedTraining(finalTrainings[0]);
                    setActiveModal('SINGLE_TRAINING');
                } else {
                    setActiveModal('TRAINING');
                }
            } else if (modalType === 'SUMMARY_DOC') {
                const summaryRes = await getSafetyResultSummary(project.id, token);
                const targetSummary = (summaryRes && summaryRes.data) ? summaryRes.data : summaryRes;
                if (targetSummary && (targetSummary.id || targetSummary.conclusion || targetSummary.content || Object.keys(targetSummary).length > 0)) {
                    setSelectedSummary(targetSummary);
                    setActiveModal('SUMMARY_DOC');
                } else {
                    alert('등록된 총평 내용이 없습니다.');
                    setActiveModal(null);
                }
            } else if (modalType === 'APPROVAL') {
                const fetchAprvData = async () => {
                    try {
                        const res = await fetch(`/api/safety/aprv/list?assessment_id=${project.id}`, {
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                        });
                        if (res.ok) {
                            const data = await res.json();
                            return (data || [])
                                .filter(doc => doc.doc_status === 'complete')
                                .sort((a, b) => {
                                    const dateA = new Date(a.updated_at || a.created_at || a.doc_date || a.draftDate || 0);
                                    const dateB = new Date(b.updated_at || b.created_at || b.doc_date || b.draftDate || 0);
                                    return dateB - dateA;
                                });
                        }
                    } catch (e) {
                        console.error('결재문서 통신 오류:', e);
                    }
                    return [];
                };

                const aprvData = await fetchAprvData();
                setModalApprovals(aprvData || []);

                if (aprvData && aprvData.length > 0) {
                    if (aprvData.length === 1) {
                        setSelectedApprovalDoc(aprvData[0]); // 유일한 결재문서
                        setActiveModal('SINGLE_APPROVAL');
                    } else {
                        setActiveModal('APPROVAL_LIST');
                    }
                } else {
                    alert('등록된 진행/완료 결재문서가 없습니다.');
                    setActiveModal(null);
                }
            } else {
                setActiveModal('TOTAL_PDF');
                setPrintSections({
                    cover: true,
                    prep: true,
                    evals: true,
                    photos: true,
                    conclusion: true,
                    meetings: true,
                    trainings: true
                });
                setReportViewerData({
                    project: project,
                    processes: [],
                    evaluations: [],
                    summary: null,
                    meetings: [],
                    trainings: [],
                    preparation: null,
                    useProcess: true,
                    approval: { lines: project.approval?.lines || [] },
                    appDocsList: []
                });
                // 종합보고서(TOTAL_PDF, FULL, SUMMARY) 조회 시에만 모든 API 데이터 병렬 호출 (전자결재 API도 병렬 요청에 병합)
                const fetchAprvData = async () => {
                    try {
                        const res = await fetch(`/api/safety/aprv/list?assessment_id=${project.id}`, {
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                        });
                        if (res.ok) {
                            const data = await res.json();
                            return (data || [])
                                .filter(doc => doc.doc_status === 'complete')
                                .sort((a, b) => {
                                    const dateA = new Date(a.updated_at || a.created_at || a.doc_date || a.draftDate || 0);
                                    const dateB = new Date(b.updated_at || b.created_at || b.doc_date || b.draftDate || 0);
                                    return dateB - dateA;
                                });
                        }
                    } catch (e) {
                        console.error('결재문서 통신 오류:', e);
                    }
                    return [];
                };

                const [projDetail, procs, evals, summary, meetings, trainings, preparation, basicInfo, aprvData] = await Promise.all([
                    getSafetyProjectDetail(project.id, token),
                    getAssessProcesses(project.id, token),
                    getAssessEvaluations(project.id, token),
                    getSafetyResultSummary(project.id, token),
                    getSafetyMeetings(project.id, token),
                    getSafetyTrainings(project.id, token),
                    getSafetyPreparation(token).catch(() => null),
                    getSafetyBasicInfo(token).catch(() => null),
                    fetchAprvData()
                ]);

                const finalProj = projDetail || project;
                const finalMeetings = Array.isArray(meetings) ? meetings : (meetings?.items || []);
                const finalTrainings = Array.isArray(trainings) ? trainings : (trainings?.items || []);
                const finalProcs = Array.isArray(procs) ? procs : (procs?.items || []);
                const finalEvals = Array.isArray(evals) ? evals : (evals?.items || []);

                // 외부 모달(SafetyReportViewerModal)로 데이터 전달
                setReportViewerData({
                    project: finalProj,
                    processes: finalProcs,
                    evaluations: finalEvals,
                    summary: summary || null,
                    meetings: finalMeetings,
                    trainings: finalTrainings,
                    preparation: preparation || null,
                    useProcess: basicInfo && basicInfo.useProcess !== undefined ? basicInfo.useProcess : true,
                    basicInfo: basicInfo || null,
                    approval: { lines: (aprvData && aprvData.length > 0) ? (aprvData[0].lines || []) : (finalProj.approval?.lines || []) },
                    appDocsList: aprvData || []
                });
            }

        } catch (err) {
            console.error('인쇄 데이터 수집 실패:', err);
            alert('데이터를 가져오는 도중 오류가 발생했습니다.');
            setActiveModal(null);
            setReportViewerShow(false);
        } finally {
            setModalLoading(false);
        }
    };




    if (loading) {
        return (
            <div className="sf-page">
                <div className="sf-loading">
                    <div className="sf-spinner"></div>
                    <span className="sf-loading-text">보고서 아카이브를 로딩 중입니다...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="sf-page sf-animate" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', background: '#ffffff' }}>
            <style>{`
        .hero-banner { background: linear-gradient(to right, #0f172a 0%, #1e293b 25%, #64748b 100%); border-radius: 12px; padding: 1.5rem 2rem; color: white; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; position: relative; overflow: hidden; }
        .kpi-wrapper { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-top: -1.25rem; padding: 0 1.5rem; position: relative; z-index: 10; }
        .kpi-box { background: white; border-radius: 12px; padding: 0.85rem 1.25rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid rgba(226, 232, 240, 0.8); }
        .hero-banner p { margin: 0; color: #94a3b8; font-size: 0.85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        @media print {
          /* 1. 배경 역할을 하는 메인 앱 루트 숨김 */
          body > #root { display: none !important; }
          /* 2. 모달 컨테이너의 포지션을 해제하여 짤리지 않게 함 */
          .print-modal-wrapper {
            position: static !important;
            background: transparent !important;
            padding: 0 !important;
            display: block !important;
            width: auto !important;
            height: auto !important;
          }
          /* 3. 모달 흰색 박스의 제한 해제 */
          .print-modal-wrapper > div {
            max-width: none !important;
            max-height: none !important;
            width: 100% !important;
            height: auto !important;
            box-shadow: none !important;
            border: none !important;
            display: block !important;
          }
          /* 4. 스크롤바가 생기지 않도록 overflow 풀기 */
          #printable-area {
            overflow: visible !important;
            height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          /* 5. 버튼 등 불필요 요소 제거 */
          .no-print { display: none !important; }
        }
        
        .a4-page {
          background: #fff;
          border-bottom: 1px dashed #cbd5e1;
          padding: 10px 0 30px 0;
          margin-bottom: 30px;
        }
        .a4-page:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }
        #printable-area table {
          font-size: 11px !important;
          margin-bottom: 20px !important;
        }
        #printable-area .photo-grid-container table {
          margin-bottom: 0px !important;
        }
        #printable-area th, #printable-area td {
          padding: 8px 6px !important;
          font-size: 11px !important;
          line-height: 1.45 !important;
          word-break: keep-all !important;
          word-wrap: break-word !important;
        }
        #printable-area td.text-start {
          text-align: left !important;
        }
        #printable-area .section-title {
          font-size: 15px !important;
          margin-bottom: 12px !important;
          border-bottom: 2px solid #0f172a !important;
          padding-bottom: 6px !important;
        }
        .sf-custom-dropdown[data-popper-placement^="bottom"] {
            margin-top: 3px !important;
        }
        .sf-custom-dropdown[data-popper-placement^="top"] {
            margin-bottom: 3px !important;
        }
      `}</style>

            {/* 헤더 카드 */}
            {/* 헤더 카드 */}
            <div className="hero-banner mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <div style={{ flex: 1, minWidth: 0, paddingRight: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <span style={{ background: '#334155', color: '#e2e8f0', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600 }}>통합 안전관리</span>
                    </div>
                    <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: 'white', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                        [{statusFilter === 'completed' ? '완료' : '진행중'}] 위험성평가 결과 보고서 및 출력 센터
                    </h3>
                    <p>
                        {statusFilter === 'completed' ? '완료된' : '진행 중인'} 위험성평가의 최종 법정 보고서 팩, 관계자 회의록, 전파 교육일지를 통합 다운로드 및 인쇄합니다.
                    </p>
                </div>
                {/* 진행/완료 캡슐형 필터 버튼 그룹 (오른쪽 끝선 기준 고정 정렬) */}
                <div style={{
                    display: 'flex',
                    justify: 'flex-end',
                    alignItems: 'center',
                    alignSelf: 'flex-start',
                    marginLeft: 'auto',
                    flexShrink: 0
                }}>
                    <div style={{
                        display: 'flex',
                        background: 'rgba(15, 23, 42, 0.4)',
                        padding: '6px',
                        borderRadius: '14px',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                        gap: '6px'
                    }}>
                        {[
                            { key: 'completed', label: '완료', icon: 'far fa-check-circle', activeBg: 'linear-gradient(135deg, #059669, #047857)', glow: '0 4px 14px rgba(5, 150, 105, 0.45)', border: '#34d399' },
                            { key: 'in_progress', label: '진행중', icon: 'far fa-play-circle', activeBg: 'linear-gradient(135deg, #2563eb, #1d4ed8)', glow: '0 4px 14px rgba(37, 99, 235, 0.45)', border: '#60a5fa' }
                        ].map(tab => {
                            const isActive = statusFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => { setStatusFilter(tab.key); setCurrentPage(1); }}
                                    style={{
                                        background: isActive ? tab.activeBg : 'rgba(255, 255, 255, 0.04)',
                                        color: isActive ? '#ffffff' : '#cbd5e1',
                                        border: isActive ? `1px solid ${tab.border}` : '1px solid transparent',
                                        padding: '7px 20px',
                                        borderRadius: '10px',
                                        fontSize: '0.92rem',
                                        fontWeight: isActive ? 800 : 500,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '7px',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: isActive ? tab.glow : 'none',
                                        letterSpacing: '-0.3px',
                                    }}
                                >
                                    <i className={tab.icon} style={{ fontSize: '1rem', opacity: isActive ? 1 : 0.75 }}></i>
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* 아카이브 표 */}
            <div className="sf-card" style={{ overflow: 'visible', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                <div className="sf-card-header" style={{ padding: '16px 22px', background: '#ffffff', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '12px' }}>
                        {/* 연도 필터 */}
                        <select
                            value={filterYear}
                            onChange={(e) => {
                                setFilterYear(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{
                                height: '38px',
                                padding: '0 32px 0 12px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                color: '#475569',
                                outline: 'none',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 12px top 50%',
                                backgroundSize: '10px auto'
                            }}
                        >
                            <option value="ALL">전체 연도</option>
                            {availableYears.map(y => (
                                <option key={y} value={y}>{y}년</option>
                            ))}
                        </select>
                        <div style={{ display: 'flex', background: '#e8edf3', padding: '4px', borderRadius: '10px', gap: 4 }}>
                            {[
                                { key: 'ALL', label: '전체', count: filteredForCount.length },
                                { key: '정기', label: '정기 평가', count: filteredForCount.filter(p => p.type === '정기').length },
                                { key: '수시', label: '수시 평가', count: filteredForCount.filter(p => p.type === '수시').length },
                            ].map(tab => {
                                const isActive = filterType === tab.key;
                                const btnStyle = isActive
                                    ? { background: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 20px', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '8px' }
                                    : { background: 'transparent', border: 'none', borderRadius: '8px', padding: '6px 20px', fontWeight: 600, transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' };

                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => { setFilterType(tab.key); setCurrentPage(1); }}
                                        style={btnStyle}
                                    >
                                        <span style={{ color: isActive ? '#003366' : '#336699', fontSize: '0.9rem' }}>{tab.label}</span>
                                        <span style={{ color: isActive ? '#475569' : '#94a3b8', fontSize: '0.8rem' }}>{tab.count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative', flexShrink: 1, minWidth: 0 }}>
                        {/* --- 인라인 검색 결과 안내 (검색조건 Select 앞) --- */}
                        {searchTerm && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '4px', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '4px 12px', borderRadius: '20px', flexShrink: 1, minWidth: 0 }}>
                                <span style={{ fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                    '
                                    <span style={{ color: '#2563eb', fontWeight: 700, maxWidth: '400px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                        {searchTerm}
                                    </span>
                                    ' 검색결과
                                </span>
                                <button
                                    onClick={() => { setSearchType('ALL'); setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
                                    style={{ background: '#bfdbfe', border: 'none', borderRadius: '50%', fontSize: '0.7rem', color: '#1e3a8a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', padding: 0, flexShrink: 0 }}
                                    title="검색 초기화"
                                >
                                    ✖
                                </button>
                            </div>
                        )}
                        <select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value)}
                            style={{ height: '38px', padding: '0 32px 0 12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', outline: 'none', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px top 50%', backgroundSize: '10px auto', flexShrink: 0 }}
                        >
                            <option value="ALL">전체</option>
                            <option value="NAME">프로젝트명</option>
                            <option value="SITE">사업장명</option>
                            <option value="MANAGER">관리감독자</option>
                        </select>
                        <input
                            type="text"
                            placeholder="검색어 입력"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    setSearchTerm(searchInput);
                                    setSearchInput('');
                                    setCurrentPage(1);
                                }
                            }}
                            style={{ width: '220px', height: '38px', padding: '0 12px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', backgroundColor: '#fff', flexShrink: 1, minWidth: '80px' }}
                        />
                        <button
                            onClick={() => { setSearchTerm(searchInput); setSearchInput(''); setCurrentPage(1); }}
                            style={{ height: '38px', padding: '0 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                        >
                            검색
                        </button>
                        <button
                            onClick={() => {
                                setSearchType('ALL');
                                setSearchInput('');
                                setSearchTerm('');
                                setFilterType('ALL');
                                setFilterYear('ALL');
                                setCurrentPage(1);
                            }}
                            style={{ height: '38px', padding: '0 16px', background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', flexShrink: 0 }}
                        >
                            초기화
                        </button>
                    </div>
                </div>

                <div style={{ padding: 0, maxWidth: '100%' }}>
                    <div className="table-responsive" style={{ width: '100%', overflowX: 'visible', overflowY: 'visible', display: 'block', WebkitOverflowScrolling: 'touch' }}>
                        <table className="sf-table text-center" style={{ fontSize: '0.88rem', minWidth: '1160px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 60, minWidth: '60px', textAlign: 'center' }}>No</th>
                                    <th style={{ width: 100, minWidth: '80px', textAlign: 'center' }}>구분</th>
                                    <th style={{ textAlign: 'left', paddingLeft: '16px', minWidth: '340px' }}>평가명</th>
                                    <th style={{ width: 200, minWidth: '160px', textAlign: 'center' }}>평가 기간</th>
                                    <th style={{ width: 100, minWidth: '90px', textAlign: 'center' }}>참여 인원</th>
                                    <th style={{ width: 150, minWidth: '100px', textAlign: 'center' }}>관리감독자</th>
                                    <th style={{ width: 120, minWidth: '95px', textAlign: 'center' }}>작성일</th>
                                    <th style={{ width: 250, minWidth: '250px', textAlign: 'center' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projects.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ padding: '48px 24px' }}>
                                            <div className="sf-empty" style={{ padding: 0 }}>
                                                <div className="sf-empty-icon" style={{ color: '#cbd5e1' }}>
                                                    {searchTerm ? <i className="fas fa-search"></i> : <i className="fas fa-folder-open"></i>}
                                                </div>
                                                <div className="sf-empty-text">
                                                    {searchTerm ? '검색어와 일치하는 결과가 없습니다.' : '등록된 위험성평가 프로젝트가 존재하지 않습니다.'}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    projects.map((proj, idx) => {
                                        const globalIdx = totalCount - ((currentPage - 1) * itemsPerPage) - idx;
                                        const createdDateStr = (proj.createdAt || proj.created_at) ? (() => {
                                            const d = new Date(proj.createdAt || proj.created_at);
                                            const y = d.getFullYear();
                                            const m = String(d.getMonth() + 1).padStart(2, '0');
                                            const day = String(d.getDate()).padStart(2, '0');
                                            return `${y}-${m}-${day}`;
                                        })() : '-';

                                        return (
                                            <tr
                                                key={proj.id}
                                                onClick={(e) => {
                                                    if (e.target.closest('button') || e.target.closest('.dropdown')) return;
                                                    navigate(`/safety/projects/${proj.id}`);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td style={{ fontWeight: 600, color: 'var(--sf-text-secondary)' }}>{globalIdx}</td>
                                                <td>
                                                    <span className={`sf-badge ${proj.type === '정기' ? 'sf-badge-primary' : 'sf-badge-danger'}`} style={{ padding: '5px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                                                        {proj.type}평가
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'left', paddingLeft: '16px', maxWidth: '300px' }}>
                                                    <div title={proj.name} style={{ fontWeight: 700, color: 'var(--sf-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {proj.name}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }} title={proj.period ? proj.period.replace(/\./g, '-') : '-'}>{proj.period ? proj.period.replace(/\./g, '-') : '-'}</td>
                                                <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text-secondary)' }}>{proj.workerCnt ? `${proj.workerCnt}명` : '-'}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {proj.status === 'completed' ? renderNamePositionBadge(proj.managerName, true) : '-'}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text-secondary)' }}>
                                                    {createdDateStr}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                                                        <button className="sf-btn sf-btn-sm" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px 10px', whiteSpace: 'nowrap', background: '#fff', border: '1px solid #c4b5fd', color: '#7c3aed', borderRadius: '6px' }} onClick={(e) => { e.stopPropagation(); setViewModalData(proj); setShowViewModal(true); }}>
                                                            <i className="fas fa-search" style={{ marginRight: 4, fontSize: '1.05rem', lineHeight: 1 }}></i>조회
                                                        </button>
                                                        <button className="sf-btn sf-btn-sm" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px 10px', whiteSpace: 'nowrap', background: '#fff', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: '6px' }} onClick={(e) => { e.stopPropagation(); handleOpenPrintModal(proj, 'TOTAL_PDF'); }}>
                                                            <i className="fas fa-file-pdf" style={{ marginRight: 4, fontSize: '1.05rem', lineHeight: 1 }}></i>종합보고서
                                                        </button>
                                                        <div className="dropdown">
                                                            <button
                                                                className="sf-btn sf-btn-sm dropdown-toggle fw-semibold"
                                                                data-bs-toggle="dropdown"
                                                                data-bs-offset="0,3"
                                                                data-bs-boundary="window"
                                                                aria-expanded="false"
                                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px 10px', whiteSpace: 'nowrap', background: '#fff', border: '1px solid #818cf8', color: '#4f46e5', borderRadius: '6px' }}
                                                            >
                                                                <i className="fas fa-folder-open" style={{ marginRight: 4, fontSize: '1.05rem', lineHeight: 1 }}></i>별지/증빙
                                                            </button>
                                                            <ul className="dropdown-menu dropdown-menu-end sf-custom-dropdown" style={{ fontSize: '0.85rem', padding: '0.5rem 0', borderRadius: '8px', border: '1px solid #94a3b8', backgroundColor: '#f8fafc', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '180px', zIndex: 9999 }}>
                                                                <li>
                                                                    <button className="dropdown-item py-2 fw-semibold" style={{ color: '#10b981' }} onClick={() => { handleOpenPrintModal(proj, 'TRAINING'); }}>
                                                                        <i className="fas fa-book me-2"></i>교육일지 (인쇄)
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button className="dropdown-item py-2 fw-semibold" style={{ color: '#3b82f6' }} onClick={() => { handleOpenPrintModal(proj, 'MINUTES'); }}>
                                                                        <i className="fas fa-file-alt me-2"></i>회의록 (인쇄)
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button className="dropdown-item py-2 fw-semibold" style={{ color: '#8b5cf6' }} onClick={() => { handleOpenPrintModal(proj, 'SUMMARY_DOC'); }}>
                                                                        <i className="fas fa-file-contract me-2"></i>총평 (인쇄)
                                                                    </button>
                                                                </li>
                                                                <li><hr className="dropdown-divider" style={{ borderColor: '#e2e8f0' }} /></li>
                                                                <li>
                                                                    <button className="dropdown-item py-2 fw-semibold" style={{ color: '#64748b' }} onClick={() => { handleOpenPrintModal(proj, 'APPROVAL'); }}>
                                                                        <i className="fas fa-file-signature me-2"></i>전자결재 인쇄
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이징 및 건수 정보 */}
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                        <div style={{
                            position: 'absolute', left: '24px',
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: '#eff6ff', border: '1px solid #bfdbfe',
                            padding: '4px 12px', borderRadius: '20px',
                            fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                        }}>
                            <span style={{ color: '#2563eb' }}>{currentPage}</span>
                            <span style={{ color: '#93c5fd', margin: '0 -2px' }}>/</span>
                            <span>{totalPages}</span>
                            <span style={{ color: '#bfdbfe', margin: '0 2px' }}>|</span>
                            <span>총 {totalCount}건</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                style={{ padding: '4px 12px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                            >
                                이전
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                                <button
                                    key={num}
                                    onClick={() => setCurrentPage(num)}
                                    style={{
                                        padding: '4px 12px',
                                        border: num === currentPage ? 'none' : '1px solid #cbd5e1',
                                        background: num === currentPage ? '#8b5cf6' : '#fff',
                                        color: num === currentPage ? '#fff' : '#475569',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                        fontWeight: num === currentPage ? 700 : 400,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {num}
                                </button>
                            ))}

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                style={{ padding: '4px 12px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                            >
                                다음
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 프로젝트 조회 모달 */}
            <SafetyProjectModal
                show={showViewModal}
                mode="view"
                initialData={viewModalData}
                token={token}
                hasPermission={false}
                onClose={() => { setShowViewModal(false); setViewModalData(null); }}
                onSaved={() => { setShowViewModal(false); setViewModalData(null); }}
            />

            {/* 종합보고서 A4 뷰어 모달 */}
            <SafetyReportViewerModal
                show={reportViewerShow}
                onClose={() => { setReportViewerShow(false); setReportViewerData(null); }}
                reportData={reportViewerData}
                isLoading={modalLoading}
                printSections={printSections}
            />

            {/* 종합보고서 출력 항목 선택 모달 */}
            <SafetyReportSectionSelectModal
                show={activeModal === 'TOTAL_PDF'}
                isLoading={modalLoading}
                onClose={() => setActiveModal(null)}
                project={selectedProj}
                onConfirm={(sections) => {
                    setPrintSections(sections);
                    setActiveModal(null);
                    setReportViewerShow(true);
                }}
            />

            {/* 회의록 목록 선택 모달 */}
            <SafetyReportMeetingListModal
                show={activeModal === 'MINUTES'}
                isLoading={modalLoading}
                onClose={() => setActiveModal(null)}
                project={selectedProj}
                meetings={displayMeetingsForModal}
                onPrintSingle={(m) => {
                    setSelectedMeeting(m);
                    setActiveModal('SINGLE_MINUTE');
                }}
                onPrintAll={() => {
                    setActiveModal('ALL_MINUTES');
                }}
            />

            {/* 회의록 단일 인쇄 모달 */}
            <SafetyReportMeetingModal
                show={activeModal === 'SINGLE_MINUTE' || activeModal === 'ALL_MINUTES'}
                onClose={() => { setActiveModal(null); setSelectedMeeting(null); }}
                project={selectedProj}
                modalSummary={activeModal === 'ALL_MINUTES' ? displayMeetingsForModal : selectedMeeting}
                aprvLines={selectedProj?.approval?.lines || []}
            />

            {/* 교육일지 목록 선택 모달 */}
            <SafetyReportTrainingListModal
                show={activeModal === 'TRAINING'}
                isLoading={modalLoading}
                onClose={() => setActiveModal(null)}
                project={selectedProj}
                trainings={displayTrainingsForModal}
                onPrintSingle={(t) => {
                    setSelectedTraining(t);
                    setActiveModal('SINGLE_TRAINING');
                }}
                onPrintAll={() => {
                    setActiveModal('ALL_TRAINING');
                }}
            />

            {/* 교육일지 단일 인쇄 모달 */}
            <SafetyReportTrainingModal
                show={activeModal === 'SINGLE_TRAINING' || activeModal === 'ALL_TRAINING'}
                onClose={() => { setActiveModal(null); setSelectedTraining(null); }}
                project={selectedProj}
                modalSummary={activeModal === 'ALL_TRAINING' ? displayTrainingsForModal : selectedTraining}
            />

            {/* 총평 단일 인쇄 모달 */}
            <SafetyReportConclusionModal
                show={activeModal === 'SUMMARY_DOC'}
                onClose={() => { setActiveModal(null); setSelectedSummary(null); }}
                project={selectedProj}
                modalSummary={selectedSummary}
            />

            {/* 전자결재 인쇄 대상 선택 모달 */}
            <SafetyReportApprovalListModal
                show={activeModal === 'APPROVAL_LIST'}
                onClose={() => setActiveModal(null)}
                project={selectedProj}
                approvals={modalApprovals}
                onPrintSingle={(doc) => {
                    setSelectedApprovalDoc(doc);
                    setActiveModal('SINGLE_APPROVAL');
                }}
                onPrintAll={(docs) => {
                    setSelectedApprovalDoc(docs); // Handle array of docs
                    setActiveModal('ALL_APPROVALS');
                }}
            />

            {/* 전자결재 단일/연속 인쇄 모달 */}
            <SafetyReportApprovalModal
                show={activeModal === 'SINGLE_APPROVAL' || activeModal === 'ALL_APPROVALS'}
                onClose={() => { setActiveModal(null); setSelectedApprovalDoc(null); }}
                appDoc={selectedApprovalDoc}
            />
        </div>
    );
}

export default SafetyResultList;
